import React, { useState } from "react";
import { openPdf, downloadCsv, money } from "../../lib/api";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { FileSpreadsheet, FileDown, ArrowUpDown } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";

export const Q = (n) => money("GTQ", n);
export const kfmt = (v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : `${Math.round(v)}`);
export const qs = (p) => new URLSearchParams(Object.entries(p).filter(([, v]) => v !== "" && v != null)).toString();

export const Kpi = ({ label, value, sub, tone = "", testid }) => (
  <div className="rounded-sm border bg-card p-4" data-testid={testid}>
    <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className={`text-2xl font-heading font-black leading-none mt-1.5 ${tone}`}>{value}</p>
    {sub && <p className="text-[10px] text-muted-foreground mt-1">{sub}</p>}
  </div>
);

export const ExportButtons = ({ tab, params, testid }) => {
  const run = async (fmt) => {
    const p = qs({ tab, fmt, ...params });
    try {
      if (fmt === "pdf") await openPdf(`/analytics/export?${p}`, `analitica_${tab}.pdf`);
      else await downloadCsv(`/analytics/export?${p}`, {}, `analitica_${tab}.xlsx`);
    } catch { toast.error("No se pudo exportar"); }
  };
  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" className="rounded-sm gap-1.5 text-xs" onClick={() => run("xlsx")} data-testid={`${testid || tab}-export-xlsx`}><FileSpreadsheet className="h-3.5 w-3.5" /> Excel</Button>
      <Button variant="outline" size="sm" className="rounded-sm gap-1.5 text-xs" onClick={() => run("pdf")} data-testid={`${testid || tab}-export-pdf`}><FileDown className="h-3.5 w-3.5" /> PDF</Button>
    </div>
  );
};

// cols: [{key, label, money?, pct?, align?}]
export const DataTable = ({ cols, rows, onRow, testid, limit = 100, empty = "Sin datos en el periodo." }) => {
  const [sort, setSort] = useState(null);
  const sorted = sort ? [...rows].sort((a, b) => {
    const x = a[sort.k], y = b[sort.k];
    const r = typeof x === "number" ? x - y : String(x || "").localeCompare(String(y || ""));
    return sort.d * r;
  }) : rows;
  const fmt = (c, v) => c.money ? Q(v) : c.pct ? `${v ?? 0}%` : (v ?? "—");
  return (
    <Card className="rounded-sm border shadow-none overflow-hidden" data-testid={testid}>
      <div className="overflow-auto max-h-[65vh]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-muted text-[11px] uppercase tracking-widest text-muted-foreground"><tr>
            <th className="px-3 py-2.5 text-left font-bold w-8">#</th>
            {cols.map((c) => (
              <th key={c.key} className={`px-3 py-2.5 font-bold whitespace-nowrap cursor-pointer select-none ${c.money || c.pct || c.num ? "text-right" : "text-left"}`}
                onClick={() => setSort((s) => ({ k: c.key, d: s?.k === c.key ? -s.d : -1 }))}>{c.label} <ArrowUpDown className="inline h-3 w-3 opacity-50" /></th>))}
          </tr></thead>
          <tbody>
            {sorted.length === 0 ? <tr><td colSpan={cols.length + 1} className="py-10 text-center text-muted-foreground">{empty}</td></tr> :
              sorted.slice(0, limit).map((r, i) => (
                <tr key={r.key || r.name || i} className={`border-t border-border ${onRow ? "cursor-pointer hover:bg-primary/5" : "hover:bg-muted/40"}`} onClick={() => onRow && onRow(r)} data-testid={`${testid}-row-${i}`}>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{i + 1}</td>
                  {cols.map((c) => <td key={c.key} className={`px-3 py-2 ${c.money || c.pct || c.num ? "text-right font-mono text-xs" : ""} ${c.cls || ""}`}>{fmt(c, r[c.key])}</td>)}
                </tr>))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export const MonthlyChart = ({ data, height = 280, testid }) => (
  <Card className="rounded-sm border shadow-none p-4" data-testid={testid}>
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={kfmt} width={42} />
        <Tooltip formatter={(v) => Q(v)} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="venta" name="Venta" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
        <Bar dataKey="costo" name="Costo" fill="#94a3b8" radius={[2, 2, 0, 0]} />
        <Line dataKey="utilidad" name="Utilidad" stroke="#16a34a" strokeWidth={2} dot={false} />
      </ComposedChart>
    </ResponsiveContainer>
  </Card>
);

export const FIN_COLS = [
  { key: "venta", label: "Venta", money: true }, { key: "costo", label: "Costo", money: true },
  { key: "utilidad", label: "Utilidad", money: true, cls: "text-green-700" }, { key: "margen", label: "Margen", pct: true },
];
